import { UI_ROOT_ELEMENT_ID } from '@upright/ui/common/constants';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetTimeoutRef } from '../../../hooks/use-get-timeout-ref';

/**
 * A hook that handles scrolling to the correct element when the element id has been
 * provided via the url hash, such as '/products/investors/#pricing' (i.e. it's an anchor link).
 *
 * This hook
 * - Listens to changes in React Router's location.
 * - When a change is detected and the new pathname has a hash in it, checks if an element with an id
 *   mathing the hash is present.
 *    - If yes, scrolls to it.
 *    - If no, fires up a DOM Mutation Observer that waits for the element to appear to DOM.
 *      - Once the element is found, scrolls to that element and stops listening to DOM mutations.
 *      - Stops listening anyway after a set delay.
 *
 * POSSIBLE ISSUES TO FIX:
 * - Will likely behave funkily on pages that have BOTH hashes ("#pricing") AND editable search/query
 *   params ("?attr=value") in the URL. As of 8/2022, this case is handled in the "StandardAnchorLinks" component
 *   by preventing the default click handler of the link so that the hash never gets appended to the URL but the
 *   scrolling works as expected.
 */

const disconnectDelayMs = 2_000;
const scrollProps: ScrollIntoViewOptions = { behavior: 'smooth' };

export function useScrollToAnchorLink() {
  const location = useLocation();
  const observer = useGetScrollElementObserver(hashToId(location.hash));
  const timeoutRef = useGetTimeoutRef();

  useEffect(() => {
    const root = document.getElementById(UI_ROOT_ELEMENT_ID);
    if (root && location.hash.length) {
      const foundElement = document.getElementById(hashToId(location.hash));
      if (foundElement) {
        // it appears that sometimes CSS takes some time to load, causing mismatched scrolling even
        // if all images on the page have aspect ratio set. to prevent that from being a problem, we
        // wait just 100 milliseconds before scrolling to the element. this is so fast that it should not
        // cause any situations where we would scroll after the user has already started scrolling
        window.setTimeout(() => {
          foundElement.scrollIntoView(scrollProps);
        }, 100);
      } else if (observer) {
        // NOTE: Only observes additions of DOM elements, does not work with dynamic element ids without specifying 'attributes: true'
        observer.observe(root, { childList: true, subtree: true });
        // We only keep the observer listening for a set delay, during which we expect the element to appear to DOM.
        timeoutRef.current = setTimeout(() => {
          observer.disconnect();
        }, disconnectDelayMs);
      }
    }
  }, [location, observer, timeoutRef]);
}

/**
 * Fires up a new instance of MutationObserver that can listen to DOM mutations and
 * scroll to an element with the provided id if that element is found among newly
 * added DOM nodes.
 *
 */
function useGetScrollElementObserver(scrollElementId: string) {
  const observer = useMemo(() => {
    if (!scrollElementId) {
      return null;
    }
    return new MutationObserver(function (mutations_list) {
      loop1: for (const mutation of mutations_list) {
        for (const added_node of mutation.addedNodes) {
          const idFound = checkIfIdPresent(added_node, scrollElementId);
          if (idFound && observer) {
            document.getElementById(scrollElementId)?.scrollIntoView(scrollProps);
            // We only want to scroll once to the first match, so after scrolling we stop listening to further DOM mutations and break out of the loop.
            observer.disconnect();
            break loop1;
          }
        }
      }
    });
  }, [scrollElementId]);
  return observer;
}

/**
 * Goes recursively through all the children of the node to see if there's an element matching the id in the subtree
 */
function checkIfIdPresent(node: Node & { id?: string }, id: string) {
  if (node.id === id) {
    return true;
  }
  if (node.childNodes.length) {
    for (const childNode of node.childNodes) {
      if (checkIfIdPresent(childNode, id)) {
        return true;
      }
    }
  }
  return false;
}

export function hashToId(hash: string) {
  return hash.replace('#', '');
}
